import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from "react";
import {
  User as FirebaseUser,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  getIdToken,
} from "firebase/auth";
import { auth } from "../config/firebase";
import { handleSessionExpiry } from "@/lib/session-handler";
import {
  AuthContextType,
  UserProfile,
  Organization,
  TaskOsBranding,
  TrialStatus,
} from "../types/auth";
import { INITIALIZE_SESSION_QUERY } from "@/graphql/auth";
import { disconnectAll as disconnectAllSseStreams } from "@/lib/sseCleanupRegistry";
import { useRuneStore } from "@/stores/runeStore";
import {
  trackAuth,
  identifyUser,
  groupOrganization,
  resetAnalytics,
} from "@/lib/analytics";
import { apiFetch } from "@/lib/apiFetch";

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [superAdminType, setSuperAdminType] = useState<
    "platform" | "partner" | "enterprise"
  >();
  const [managedOrganizations, setManagedOrganizations] = useState<
    Organization[]
  >([]);
  const [currentOrganization, setCurrentOrganization] =
    useState<Organization | null>(null);
  const [trialStatus, setTrialStatus] = useState<TrialStatus | null>(null);
  const [shouldShowOnboarding, setShouldShowOnboarding] = useState<boolean | null>(null);
  const trialTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Helper to calculate trial status from expiration time
  const calculateTrialStatus = useCallback(
    (
      isTrial: boolean,
      trialExpiresAt: number | undefined,
      trialId: string | undefined,
    ): TrialStatus | null => {
      if (!isTrial) {
        return null;
      }

      const expiresAt = trialExpiresAt
        ? new Date(trialExpiresAt * 1000)
        : null;
      const now = Date.now();
      const expiresAtMs = expiresAt ? expiresAt.getTime() : 0;
      const timeRemainingMs = expiresAt ? Math.max(0, expiresAtMs - now) : null;
      const isExpired = expiresAt ? now >= expiresAtMs : false;

      return {
        isTrial: true,
        isExpired,
        expiresAt,
        trialId: trialId || null,
        timeRemainingMs: isExpired ? 0 : timeRemainingMs,
      };
    },
    [],
  );

  // Clear trial timer
  const clearTrialTimer = useCallback(() => {
    if (trialTimerRef.current) {
      clearInterval(trialTimerRef.current);
      trialTimerRef.current = null;
    }
  }, []);

  // Start trial countdown timer
  const startTrialTimer = useCallback(
    (trialExpiresAt: number, trialId: string) => {
      clearTrialTimer();

      // Update every minute
      trialTimerRef.current = setInterval(() => {
        const newStatus = calculateTrialStatus(true, trialExpiresAt, trialId);
        setTrialStatus(newStatus);

        // Stop the timer if trial has expired
        if (newStatus?.isExpired) {
          clearTrialTimer();
        }
      }, 60000); // Update every minute
    },
    [calculateTrialStatus, clearTrialTimer],
  );

  /**
   * Fetch user profile using batched initializeSession query.
   * This replaces 5 sequential requests with a single request:
   * 1. getIdToken() - still needed for auth header
   * 2. refreshMyClaims mutation - now part of initializeSession
   * 3. getIdTokenResult(true) - only called if needsRefresh is true
   * 4. currentUser query - now part of initializeSession
   * 5. managedOrganizations query - now part of initializeSession
   */
  const fetchUserProfile = async (firebaseUser: FirebaseUser) => {
    try {
      // Get token for authorization header
      const token = await getIdToken(firebaseUser);

      // Use direct fetch for initial auth to avoid graphql-client middleware race condition
      // The middleware may not have access to auth.currentUser yet during initial load.
      // URL matches graphql-client.ts — LB-fronted /v1/core/graphql.
      const response = await apiFetch(`/v1/core/graphql`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ query: INITIALIZE_SESSION_QUERY }),
      });

      // Check for HTTP-level errors (401, 403, etc) before parsing JSON
      if (!response.ok) {
        // On 401, trigger session expiry handler to sign out and redirect
        if (response.status === 401) {
          await handleSessionExpiry();
          return; // Stop processing, user will be redirected to login
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const json = await response.json();
      if (json.errors) {
        throw new Error(json.errors[0]?.message || "GraphQL error");
      }

      // Ensure the response has the expected data structure
      if (!json.data?.initializeSession) {
        throw new Error("Invalid response: missing initializeSession data");
      }

      const result = json.data as {
        initializeSession: {
          user: {
            id: string;
            email: string;
            username: string | null;
            fullName: string;
            roleType: string | null;
            department: string | null;
            isSuperuser: boolean;
            superAdminType: string | null;
            organizationId: string;
            organizationName: string;
            organizationBranding?: TaskOsBranding | null;
          };
          shouldShowOnboarding: boolean;
          claimsStatus: {
            needsRefresh: boolean;
            isTrial: boolean;
            trialExpiresAt?: number;
            trialId?: string;
          };
          managedOrganizations?: Organization[];
        };
      };

      const { user: profile, claimsStatus, managedOrganizations: managedOrgs } = result.initializeSession;

      // Only refresh Firebase token if claims were updated on the backend
      if (claimsStatus.needsRefresh) {
        console.log("Claims updated, refreshing token...");
        await getIdToken(firebaseUser, true);
      }

      // Set superuser state
      const isSuper = Boolean(profile.isSuperuser);
      const adminType = profile.superAdminType as
        | "platform"
        | "partner"
        | "enterprise"
        | undefined;

      setIsSuperuser(isSuper);
      setSuperAdminType(adminType);

      // Set onboarding state from batched response
      setShouldShowOnboarding(result.initializeSession.shouldShowOnboarding);

      // Calculate and set trial status from claimsStatus
      const newTrialStatus = calculateTrialStatus(
        claimsStatus.isTrial,
        claimsStatus.trialExpiresAt,
        claimsStatus.trialId,
      );
      setTrialStatus(newTrialStatus);

      // Start timer if user is on an active trial
      if (claimsStatus.isTrial && claimsStatus.trialExpiresAt && claimsStatus.trialId && !newTrialStatus?.isExpired) {
        startTrialTimer(claimsStatus.trialExpiresAt, claimsStatus.trialId);
      } else {
        clearTrialTimer();
      }

      // Build organization from profile
      let userOrganization: Organization | null = null;
      const org: Organization | undefined = profile.organizationId
        ? {
            id: profile.organizationId,
            name: profile.organizationName || profile.organizationId,
            description: undefined,
            domain: undefined,
            taskOsBranding: profile.organizationBranding ?? null,
          }
        : undefined;

      setUserProfile({
        ...profile,
        username: profile.username || "",
        isSuperuser: isSuper,
        superAdminType: adminType,
        organization: org,
      });
      setOrganization(org || null);
      setCurrentOrganization(org || null);
      userOrganization = org || null;

      // PostHog identify + group analytics. Tier is derived from
      // trialStatus — orgs on an active trial bucket as "trial", everyone
      // else "active". This is the only place backend events can match
      // tier on, so keep the mapping centralized here.
      const tier = newTrialStatus?.isTrial ? "trial" : "active";
      identifyUser(profile.id, { email: profile.email, tier });
      if (org?.id) {
        groupOrganization(org.id, { name: org.name, tier });
      }

      // Handle managed organizations for superusers (already fetched in batched query)
      if (isSuper && managedOrgs && managedOrgs.length > 0) {
        setManagedOrganizations(managedOrgs);

        const storedOrgId = sessionStorage.getItem("currentOrganizationId");
        if (storedOrgId) {
          if (storedOrgId === "all") {
            setCurrentOrganization({
              id: "all",
              name: "All Organizations",
            });
          } else {
            const storedOrg = managedOrgs.find(
              (o: Organization) => o.id === storedOrgId,
            );
            if (storedOrg) {
              setCurrentOrganization(storedOrg);
            } else if (userOrganization?.id) {
              sessionStorage.setItem(
                "currentOrganizationId",
                userOrganization.id,
              );
              setCurrentOrganization(userOrganization);
            }
          }
        } else if (userOrganization?.id) {
          sessionStorage.setItem(
            "currentOrganizationId",
            userOrganization.id,
          );
          setCurrentOrganization(userOrganization);
        }
      } else {
        setManagedOrganizations([]);

        if (userOrganization?.id) {
          sessionStorage.setItem("currentOrganizationId", userOrganization.id);
          setCurrentOrganization(userOrganization);
        }
      }
    } catch (error) {
      console.error("Failed to initialize session:", error);
      setUserProfile({
        id: firebaseUser.uid,
        email: firebaseUser.email || "",
        username: firebaseUser.email?.split("@")[0] || "",
        roleType: "business_user",
      });
      setIsSuperuser(false);
      setManagedOrganizations([]);
      setTrialStatus(null);
      setShouldShowOnboarding(null);
      clearTrialTimer();
    }
  };

  const refreshUserProfile = async () => {
    if (user) {
      await fetchUserProfile(user);
    }
  };

  const switchOrganization = async (orgId: string) => {
    if (!isSuperuser) {
      throw new Error("Organization switching requires super-admin access");
    }

    if (orgId === "all") {
      sessionStorage.setItem("currentOrganizationId", "all");
      setCurrentOrganization({ id: "all", name: "All Organizations" });
      return;
    }

    const targetOrg = managedOrganizations.find((org) => org.id === orgId);
    if (!targetOrg) {
      throw new Error("Organization access denied");
    }

    sessionStorage.setItem("currentOrganizationId", orgId);
    setCurrentOrganization(targetOrg);
    groupOrganization(orgId, { name: targetOrg.name });
  };

  /**
   * Soft org switch for /* surfaces. Tears down every org-scoped
   * source of state in a single controlled order so no Org A data can
   * land in Org B's view:
   *
   *   1. Close every registered SSE/WebSocket stream (Ignite pipeline
   *      logs, execution events, project progress, Rune SSE — via the
   *      AbortController abort triggered by runeStore.reset below).
   *   2. Reset zustand stores that hold org-scoped state (runeStore).
   *      runeStore.reset() also aborts the in-flight Rune fetch.
   *   3. Clear the React Query cache via the caller-supplied callback
   *      (we inject the QueryClient from the component tree to avoid
   *      a hard dependency on @tanstack/react-query here).
   *   4. Flip sessionStorage + currentOrganization state.
   *   5. Fire the optional `onComplete` callback for navigation.
   *
   * Kept in this order because each step relies on the previous:
   * aborting streams first means no late-landing responses race with
   * the store reset; clearing stores next means the cache clear can't
   * be re-populated by pending store subscriptions.
   */
  const softSwitchOrganization = async (
    orgId: string,
    options: {
      clearQueryCache: () => void;
      onComplete?: () => void;
    },
  ) => {
    if (!isSuperuser) {
      throw new Error("Organization switching requires super-admin access");
    }

    const targetOrg = managedOrganizations.find((org) => org.id === orgId);
    if (!targetOrg) {
      throw new Error("Organization access denied");
    }

    // 1. Tear down open streams.
    disconnectAllSseStreams();

    // 2. Reset org-scoped zustand stores. runeStore.reset() also
    //    aborts the in-flight Rune SSE fetch via AbortController.
    useRuneStore.getState().reset();

    // 3. Wipe React Query cache so Org A responses aren't replayed.
    options.clearQueryCache();

    // 4. Switch active org — graphqlClient reads currentOrganizationId
    //    from sessionStorage on every request, so this takes effect
    //    immediately for any refetch triggered by the cache clear.
    sessionStorage.setItem("currentOrganizationId", orgId);
    setCurrentOrganization(targetOrg);
    groupOrganization(orgId, { name: targetOrg.name });

    trackAuth("soft_switch_completed", {
      from_org_id: currentOrganization?.id ?? null,
      to_org_id: orgId,
    });

    // 5. Let the caller navigate (e.g. `navigate("/discover")`).
    options.onComplete?.();
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        await fetchUserProfile(firebaseUser);
      } else {
        setUserProfile(null);
        setOrganization(null);
        setIsSuperuser(false);
        setSuperAdminType(undefined);
        setManagedOrganizations([]);
        setCurrentOrganization(null);
        setTrialStatus(null);
        setShouldShowOnboarding(null);
        clearTrialTimer();
      }

      setLoading(false);
    });

    return () => {
      unsubscribe();
      clearTrialTimer();
    };
  }, [clearTrialTimer]);

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signUp = async (email: string, password: string) => {
    await createUserWithEmailAndPassword(auth, email, password);
  };

  const signOut = async () => {
    sessionStorage.removeItem("currentOrganizationId");
    resetAnalytics();
    await firebaseSignOut(auth);
  };

  const getCurrentToken = async (): Promise<string | null> => {
    try {
      if (user) {
        const token = await getIdToken(user, true);
        return token;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const value: AuthContextType = {
    user,
    userProfile,
    organization,
    loading,
    signIn,
    signUp,
    signOut,
    refreshUserProfile,
    getCurrentToken,
    isSuperuser,
    superAdminType,
    managedOrganizations,
    currentOrganization,
    switchOrganization,
    softSwitchOrganization,
    trialStatus,
    isTrialUser: trialStatus?.isTrial ?? false,
    isTrialExpired: trialStatus?.isExpired ?? false,
    shouldShowOnboarding,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
